import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router';

import RouteNames from '@/router/routes';

import SharedLayout from '@/components/sharedLayout';
import LoaderSpinner from '@/components/loaderSpinner';

const Main = lazy(() => import('@/pages/main'));

const VpsFree = lazy(() => import('@/pages/vpsFree'));
const VpsPaid = lazy(() => import('@/pages/vpsPaid'));
const VpsManual = lazy(() => import('@/pages/vpsManual'));
const Guide = lazy(() => import('@/pages/guide'));
const Video = lazy(() => import('@/pages/video'));

const Monitoring = lazy(() => import('@/pages/monitoring'));
const MonitoringManual = lazy(() => import('@/pages/monitoringManual'));
const MonitoringDetails = lazy(() => import('@/pages/monitoringDetails'));

const ManualArticle = lazy(() => import('@/pages/manualArticle'));

const Rebate = lazy(() => import('@/pages/rebate'));
const RebateBrokers = lazy(() => import('@/pages/rebateBrokers'));

const PartnerClassic = lazy(() => import('@/pages/partnerClassic'));
const PartnerMultilevel = lazy(() => import('@/pages/partnerMultilevel'));
const WhiteLabel = lazy(() => import('@/pages/whiteLabel'));
const Resale = lazy(() => import('@/pages/resale'));
const PartnerArticle = lazy(() => import('@/pages/partnerArticle'));

const Rating = lazy(() => import('@/pages/rating'));
const RatingBroker = lazy(() => import('@/pages/ratingBroker'));
const Courses = lazy(() => import('@/pages/courses'));
const Course = lazy(() => import('@/pages/course'));
const CourseArticle = lazy(() => import('@/pages/courseArticle'));
const Robot = lazy(() => import('@/pages/robot'));
const Glossary = lazy(() => import('@/pages/glossary'));
const GlossaryArticle = lazy(() => import('@/pages/glossaryArticle'));

const AboutUs = lazy(() => import('@/pages/aboutUs'));

const PublicOffer = lazy(() => import('@/pages/publicOffer'));
const PrivacyPolicy = lazy(() => import('@/pages/privacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/termsOfService'));

const NotFound = lazy(() => import('@/pages/notFound'));

function Router() {
  const { pathname } = useLocation();

  const routes = (
    <Route element={<SharedLayout />}>
      <Route path={RouteNames.MAIN} element={<Main />} />

      <Route path={RouteNames.VPS_FREE} element={<VpsFree />} />
      <Route path={RouteNames.VPS_PAID} element={<VpsPaid />} />
      <Route path={RouteNames.VPS_PAID_REDIRECT} element={<VpsPaid />} />

      <Route path={RouteNames.VPS_MANUAL}>
        <Route index element={<VpsManual />} />
        <Route path={RouteNames.ARTICLE} element={<ManualArticle />} />
      </Route>
      <Route path={RouteNames.VPS_MANUAL_REDIRECT}>
        <Route index element={<VpsManual />} />
        <Route path={RouteNames.ARTICLE} element={<ManualArticle />} />
      </Route>

      <Route path={RouteNames.GUIDE}>
        <Route index element={<Guide />} />
        <Route path={RouteNames.ARTICLE} element={<ManualArticle />} />
      </Route>
      <Route path={RouteNames.GUIDE_REDIRECT}>
        <Route index element={<Guide />} />
        <Route path={RouteNames.ARTICLE} element={<ManualArticle />} />
      </Route>

      <Route path={RouteNames.VIDEO} element={<Video />} />
      <Route path={RouteNames.VIDEO_REDIRECT} element={<Video />} />

      <Route path={RouteNames.MONITORING}>
        <Route index element={<Monitoring />} />
        <Route
          path={RouteNames.MONITORING_DETAILS}
          element={<MonitoringDetails />}
        />
      </Route>

      <Route path={RouteNames.MONITORING_MANUAL}>
        <Route index element={<MonitoringManual />} />
        <Route path={RouteNames.ARTICLE} element={<ManualArticle />} />
      </Route>

      <Route path={RouteNames.REBATE} element={<Rebate />} />
      <Route path={RouteNames.REBATE_REDIRECT} element={<Rebate />} />
      <Route path={RouteNames.REBATE_BROKERS} element={<RebateBrokers />} />

      <Route path={RouteNames.PARTNER_CLASSIC} element={<PartnerClassic />} />
      <Route
        path={RouteNames.PARTNER_CLASSIC_REDIRECT}
        element={<PartnerClassic />}
      />

      <Route
        path={RouteNames.PARTNER_MULTILEVEL}
        element={<PartnerMultilevel />}
      />

      <Route path={RouteNames.WHITE_LABEL} element={<WhiteLabel />} />
      <Route path={RouteNames.WHITE_LABEL_REDIRECT} element={<WhiteLabel />} />

      <Route path={RouteNames.RESALE}>
        <Route index element={<Resale />} />
        <Route path={RouteNames.ARTICLE} element={<PartnerArticle />} />
      </Route>
      <Route path={RouteNames.RESALE_REDIRECT}>
        <Route index element={<Resale />} />
        <Route path={RouteNames.ARTICLE} element={<PartnerArticle />} />
      </Route>

      <Route path={RouteNames.RATING}>
        <Route index element={<Rating />} />
        <Route path={RouteNames.BROKER} element={<RatingBroker />} />
      </Route>

      <Route path={RouteNames.COURSES}>
        <Route index element={<Courses />} />
        <Route path={RouteNames.COURSE}>
          <Route index element={<Course />} />
          <Route path={RouteNames.ARTICLE} element={<CourseArticle />} />
        </Route>
      </Route>
      <Route path={RouteNames.COURSES_REDIRECT}>
        <Route index element={<Courses />} />
        <Route path={RouteNames.COURSE}>
          <Route index element={<Course />} />
          <Route path={RouteNames.ARTICLE} element={<CourseArticle />} />
        </Route>
      </Route>

      <Route path={RouteNames.ROBOT} element={<Robot />} />
      <Route path={RouteNames.ROBOT_REDIRECT} element={<Robot />} />
      <Route path={RouteNames.GLOSSARY}>
        <Route index element={<Glossary />} />
        <Route path={RouteNames.ARTICLE} element={<GlossaryArticle />} />
      </Route>
      <Route path={RouteNames.GLOSSARY_REDIRECT}>
        <Route index element={<Glossary />} />
        <Route path={RouteNames.ARTICLE} element={<GlossaryArticle />} />
      </Route>

      <Route path={RouteNames.ABOUT_US} element={<AboutUs />} />

      <Route path={RouteNames.PUBLIC_OFFER} element={<PublicOffer />} />
      <Route path={RouteNames.PRIVACY_POLICY} element={<PrivacyPolicy />} />
      <Route path={RouteNames.TERMS_OF_SERVICE} element={<TermsOfService />} />

      {/* <Route path={RouteNames.ANY} element={<Navigate to="/" replace />} /> */}
      <Route path={RouteNames.ANY} element={<NotFound />} />
    </Route>
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Suspense fallback={<LoaderSpinner />}>
      <Routes>{routes}</Routes>
    </Suspense>
  );
}

export default Router;
